/**
 * Kapitel „Hebel" der Sektion „Anfos & Umsatz" (/auswertungen/hebel), P4 schlank (Plan 2026-10-27-p4-hebel-p5-kern,
 * Paket A): Heat-Up-Wellen je Webinar, Nachfass je Publisher, Kunden nach Zahl ihrer Anfos. Persona-Umbau A4 (Plan
 * 2026-10-28): Seitenkopf mit drei Kacheln aus denselben Rechnungen wie die Karten.
 * Backend: /api/auswertungen/hebel/*. Karten immer offen (Till 23.10.2026).
 */
import { FC, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import ChapterPage from '../../../components/auswertungen/ChapterPage';
import Explainer, { ExplainerSection } from '../../../components/Explainer';
import { fest, int, Kachel, useLaden } from '../../../components/auswertungen/kennzahlTeile';
import type { KanalFensterMonate } from '../../../helpers/auswertungenData';
import { CrossSellAntwort, fetchCrossSell, fetchNachfass, fetchWellen, NachfassAntwort } from '../../../helpers/hebelData';
import { formatPct, GLOSSAR, HEBEL_FRAGE, TERMS } from '../../../helpers/kpiDefinitions';
import { euroJe1000, euroKurz, nachfassLuecke, nachfassOhneSchaetzung } from './befund';
import { germanNow } from '../../../helpers/germanTime';
import { formatNumber } from '../../../helpers/numberUtils';
import CrossSellKarte, { mehrfachKern } from './CrossSellKarte';
import NachfassKarte, { nachfassBefundEingabe } from './NachfassKarte';
import { tag } from './teile';
import WellenKarte from './WellenKarte';
import MatrixKarte from './MatrixKarte';

/** Fenster des Seitenkopfs: fest 6 Monate (die Kartenwahl 3/6/12 gilt nur für die Karte). */
const KOPF_MONATE: KanalFensterMonate = 6;
const fetchNachfassKopf = () => fetchNachfass(KOPF_MONATE);

/**
 * Lädt je Fenster, lässt die alten Daten stehen, bis die neuen da sind (A1: sonst baute sich die Tabelle beim Wechsel
 * 3/6/12 neu auf und vergaß ihre Sortierung). `laedt` = neue Daten unterwegs; ein Fehler ersetzt die alten Daten.
 */
function useFensterHalten<T>(fetcher: (m: KanalFensterMonate) => Promise<T>, monate: KanalFensterMonate) {
  const [stand, setStand] = useState<{ data: T | null; fehler: string | null; monate: KanalFensterMonate | null }>({
    data: null,
    fehler: null,
    monate: null,
  });
  useEffect(() => {
    let abgebrochen = false;
    fetcher(monate)
      .then((d) => {
        if (!abgebrochen) setStand({ data: d, fehler: null, monate });
      })
      .catch((e: Error) => {
        if (!abgebrochen) setStand({ data: null, fehler: e.message, monate });
      });
    return () => {
      abgebrochen = true;
    };
  }, [fetcher, monate]);
  return { data: stand.data, fehler: stand.fehler, laedt: stand.monate !== monate && stand.data !== null };
}

const minusTag = (v: string): string => {
  const [j, m, t] = v.split('-').map(Number);
  return new Date(Date.UTC(j, m - 1, t - 1)).toISOString().slice(0, 10);
};

/** A4: drei Kacheln unter dem Titel — dieselben Rechnungen wie die Karten darunter (Befund, Kern der Cross-Sell-Karte). */
const HebelKopf: FC<{ nachfass: NachfassAntwort | null; crossSell: CrossSellAntwort | null; fehler: boolean }> = ({ nachfass, crossSell, fehler }) => {
  const leer = fehler ? '—' : '…';
  const fenster = nachfass ? `${KOPF_MONATE} Monate, ${tag(nachfass.window.von)}–${tag(minusTag(nachfass.window.versand_bis))}` : '';
  const eingabe = nachfass ? nachfassBefundEingabe(nachfass) : null;
  const sch = eingabe ? nachfassOhneSchaetzung(eingabe) : null;
  const luecke = eingabe ? nachfassLuecke(eingabe, germanNow().format('YYYY-MM-DD')) : null;
  const k = crossSell ? mehrfachKern(crossSell) : null;
  const ohneZahl = nachfass ? nachfass.webinare.ohne_nachfass.length : 0;
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 2, mb: 3 }} aria-label="Kennzahlen des Kapitels">
      {/* Runde 2: kein Vergleich mit Heat-Ups (anderer Nenner, legte eine Umschichtung nahe), sondern der Ertrag je Webinar. */}
      <Kachel
        wert={nachfass ? fest(euroJe1000(nachfass.summe.wert_je_1000)) : leer}
        label={`Nachfass: ${TERMS.nachfassWertJe1000.label} (an Angemeldete)`}
        tip={TERMS.nachfassWertJe1000.tip}
        sub={
          nachfass ? (
            <>
              {sch && sch.z > 0
                ? `Ø ${formatNumber(sch.z, 1)} Anfos${eingabe?.wert_je_anfo == null ? '' : ` / ≈ ${euroKurz(sch.z * eingabe.wert_je_anfo)}`} je Webinar mit Nachfass · `
                : ''}
              {fenster}
            </>
          ) : undefined
        }
      />
      <Kachel
        wert={nachfass ? `${int(ohneZahl)} von ${int(nachfass.webinare.im_fenster)}` : leer}
        label={TERMS.nachfassOhne.label}
        tip={TERMS.nachfassOhne.tip}
        sub={
          nachfass ? (
            <>
              {ohneZahl === 0
                ? 'jedes Webinar des Zeitraums mit Nachfass · '
                : luecke && !luecke.aktuell && luecke.letzte
                  ? `alle bis ${tag(luecke.letzte)} — seitdem jedes Webinar mit Nachfass · `
                  : `zuletzt am ${tag(luecke?.letzte ?? '')}${sch && sch.anfos > 0 ? `, höchstens ≈ ${int(Math.round(sch.anfos))} Anfos (Schätzung)` : ''} · `}
              Start {tag(nachfass.webinare_ab)}–{tag(minusTag(nachfass.window.versand_bis))}
            </>
          ) : undefined
        }
      />
      <Kachel
        wert={crossSell ? fest(formatPct(k?.umsatzAnteil ?? null, 0)) : leer}
        label={TERMS.kundenMehrfachUmsatz.label}
        tip={TERMS.kundenMehrfachUmsatz.tip}
        sub={crossSell ? `${fest(formatPct(k?.kundenAnteil ?? null, 0))} der Kunden · alle Anfos seit Beginn der Daten` : undefined}
      />
    </Box>
  );
};

const EXPLAINER: ExplainerSection[] = [
  {
    label: 'Datengrundlage',
    body: 'Webinar-Versände mit Empfängern, Anmeldungen, Teilnahme und Erst-Kontakt-Anfos; die Nachfass-Gruppen mit ihren Adressen; PL-X-Bestellungen mit Zahlungen. Gezählt werden dieselben Anfos wie überall in der Sektion (ohne Stornos und Testbestellungen).',
  },
  {
    label: 'Berechnung',
    body: 'Wellen: alle Heat-Ups eines Zeitfensters zu einem Webinar zusammen, Anfos beim ersten Kontakt einer Person; Euro = Anfos × mittlerer Erwarteter Wert der Anfos ihres Webinars. Nachfass: Anfos über die Kennung im Bestell-Link, je Publisher aus dieser Kennung, Euro = Erwarteter Wert jeder Anfo; Empfänger beim Publisher ihrer ersten Anmeldung. Kunden: jede Adresse einmal, alle Anfos seit Beginn der Daten (verweigerte Bestellungen zählen nicht).',
  },
  {
    label: 'Aussage',
    body: 'Ob jede Einladungs-Welle rausging und was sie bringt, ob der Nachfass alle Listen erreicht und wie gut er je Liste wirkt, und wie viel Umsatz an Kunden mit mehreren Anfos hängt. Jede Karte beginnt mit einem Befund: was die Zahlen zeigen und was man testen könnte — keine Ursache.',
  },
  {
    label: 'Wichtig',
    highlight: true,
    body: 'Jede Karte hat ihre eigene Zuordnung — Zahlen verschiedener Karten nicht addieren. Wellen und Nachfass rechnen mit dem Erwarteten Wert (bei Bestellung) wie das Kapitel Kanäle; je Welle eines einzelnen Webinars kein Euro (zu wenige Anfos). Kunden-Euro ist echtes Geld und wächst mit dem Alter der Kunden.',
  },
];

const Hebel: FC = () => {
  const [monate, setMonate] = useState<KanalFensterMonate>(6);
  const wellen = useLaden(fetchWellen);
  const nachfass = useFensterHalten(fetchNachfass, monate);
  const kopfNachfass = useLaden(fetchNachfassKopf);
  const crossSell = useLaden(fetchCrossSell);
  return (
    <ChapterPage
      title="Hebel"
      question={HEBEL_FRAGE}
      intro={
        <Typography component="span" variant="body2" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
          {TERMS.welle.glossar} · {GLOSSAR.heatUp} · {GLOSSAR.nachfass}
        </Typography>
      }
      standQuellen={[
        { key: 'plx_orders', label: 'Bestellungen und Zahlungen (PL-X)' },
        { key: 'webinar_send_metrics', label: 'Webinar-Versände' },
      ]}
    >
      <HebelKopf nachfass={kopfNachfass.data} crossSell={crossSell.data} fehler={Boolean(kopfNachfass.fehler || crossSell.fehler)} />
      <Explainer title="Wie dieses Kapitel rechnet" sections={EXPLAINER} />
      <WellenKarte data={wellen.data} fehler={wellen.fehler} />
      {/* Workshop-Lücke N2 (Plan 2026-10-29): Liste × Welle direkt unter den Wellen, aus derselben Antwort. */}
      <MatrixKarte data={wellen.data} fehler={wellen.fehler} />
      <NachfassKarte data={nachfass.data} fehler={nachfass.fehler} monate={monate} onMonate={setMonate} laedt={nachfass.laedt} />
      <CrossSellKarte data={crossSell.data} fehler={crossSell.fehler} />
    </ChapterPage>
  );
};

export default Hebel;
